import { Search, PlusCircle, SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { AddCandidateDialog } from "./AddCandidateDialog";

interface CandidateFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusChange: (value: string) => void;
  disabled?: boolean;
  onCandidateAdded?: (candidateId: string) => void;
}

const statusLabels: Record<string, string> = {
  new: "New",
  contacted: "Contacted",
  interview: "Interview",
  offer: "Offer",
  hired: "Hired",
  rejected: "Rejected",
  onhold: "On Hold",
};

export const CandidateFilters = ({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusChange,
  disabled = false,
  onCandidateAdded,
}: CandidateFiltersProps) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const handleCandidateCreated = (candidateId: string) => {
    onCandidateAdded?.(candidateId);
    setIsAddDialogOpen(false);
  };

  const handleClearSearch = () => {
    onSearchChange("");
  };

  const hasActiveFilters = statusFilter !== "all" || searchQuery.trim() !== "";
  
  return (
    <>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
        <div className="relative w-full sm:w-[250px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search candidates..."
            className="pl-8 pr-8 w-full"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            disabled={disabled}
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-full w-8 rounded-l-none text-muted-foreground hover:text-foreground"
              onClick={handleClearSearch}
              disabled={disabled}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Clear search</span>
            </Button>
          )}
        </div>
        
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Select
            value={statusFilter}
            onValueChange={onStatusChange}
            disabled={disabled}
          >
            <SelectTrigger className="w-full sm:w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(statusLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" className="relative" disabled={disabled}>
                <SlidersHorizontal className="h-4 w-4" />
                {hasActiveFilters && (
                  <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary" />
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-4" align="end">
              <div className="space-y-4">
                <h4 className="font-medium">Active Filters</h4>
                <div className="space-y-2">
                  {hasActiveFilters ? (
                    <div className="space-y-2">
                      {statusFilter !== "all" && (
                        <div className="flex items-center justify-between">
                          <Badge variant="outline">Status: {statusLabels[statusFilter]}</Badge>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6" 
                            onClick={() => onStatusChange("all")}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                      {searchQuery && (
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="truncate max-w-[120px]">
                            Search: {searchQuery}
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6" 
                            onClick={handleClearSearch}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">No active filters</div>
                  )}
                </div>
                
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      onStatusChange("all");
                      onSearchChange("");
                    }}
                  >
                    <X className="h-3 w-3 mr-2" />
                    Clear All Filters
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>

          <Button 
            className="w-full sm:w-auto" 
            disabled={disabled}
            onClick={() => setIsAddDialogOpen(true)}
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Add Candidate
          </Button>
        </div>
      </div>

      <AddCandidateDialog 
        open={isAddDialogOpen} 
        onOpenChange={setIsAddDialogOpen}
        onCandidateCreated={handleCandidateCreated}
      />
    </>
  );
};

export { statusLabels };
